import {
	ActionIcon,
	Checkbox,
	Group,
	Select,
	Stack,
	Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { Prism } from '@mantine/prism';
import { Button, Icon, Text } from '@repo/foundations';
import { entries, mapValues, pickBy } from 'lodash-es';
import type { Language } from 'prism-react-renderer';
import { useEffect, useState } from 'react';
import { useCallIntegrationApiMethod } from '../../../api/hooks/inspectApi';
import type { IIntegratinApiSpec } from '../../../api/types/models/integrationApiSpec';

export function InspectAPIBlock({
	spec,
	integrationId,
	onDelete,
}: {
	spec: IIntegratinApiSpec;
	integrationId: string;
	onDelete: () => void;
}) {
	const methodNameAndSpecs = entries(spec.api_spec);
	const [pickedMethodName, setPickedMethodName] = useState(
		methodNameAndSpecs[0][0]
	);

	const [result, setResult] = useState('');
	const [showOriginal, setShowOriginal] = useState(false);
	const [runAsBackgroundJob, setRunAsBackgroundJob] = useState(false);
	const [collapsed, setCollapsed] = useState(false);
	const [language, setLanguage] = useState<string | null>('json');

	const resultTooLong = result.split('\n').length > 6;
	const forcePreview =
		result.split('\n').length > 1500 || result.length > 10000;

	const modifiedResult =
		(collapsed && resultTooLong) || forcePreview
			? `${result.split('\n').slice(0, 6).join('\n')}\n\n\n .....\n ${
					forcePreview
						? 'Response too large to be rendered, please download to see the full result'
						: 'Expand to see more'
				}`
			: result;

	const methodSpec = spec.api_spec[pickedMethodName].argument_spec;

	const form = useForm({
		initialValues: mapValues(methodSpec, () => ''),
		validate: mapValues(methodSpec, () => (value: string) => {
			try {
				JSON.parse(value);
			} catch (e) {
				return 'Invalid JSON value, string must be quoted in "';
			}
			return null;
		}),
	});

	useEffect(() => {
		form.setValues({});
	}, [pickedMethodName]);

	const { mutateAsync: callMethod, isPending: isCalling } =
		useCallIntegrationApiMethod();

	const run = async () => {
		if (form.validate().hasErrors) {
			return;
		}

		const r = await callMethod({
			integrationId,
			method: pickedMethodName,
			payload: mapValues(
				pickBy(form.values, (_, k) => k in methodSpec),
				(value) => JSON.parse(value)
			),
			showOriginal,
			runAsBackgroundJob,
		});

		if (r.status === 'failed') {
			setResult(r.error);
		} else {
			if (typeof r.result === 'string') {
				setResult(r.result);
			} else {
				setResult(JSON.stringify(r.result, null, 2));
			}
		}
	};

	const downloadJson = () => {
		const blob = new Blob([result], { type: 'text/plain' });
		const jsonUrl = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = jsonUrl;
		link.download = `${integrationId}_${pickedMethodName}.txt`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	return (
		<Group align="start" noWrap maw="100%">
			<Stack
				sx={{
					width: '25vw',
				}}
			>
				<Text size="sm" weight="semibold">
					Pick a method and fill in arguments
				</Text>
				<Group w="100%" noWrap>
					<Select
						sx={{
							flex: 1,
						}}
						placeholder="Pick one"
						value={pickedMethodName}
						onChange={(value) => setPickedMethodName(value as string)}
						dropdownPosition="bottom"
						data={entries(spec.api_spec).map(([key, _]) => ({
							value: key,
							label: key,
						}))}
					/>
					<ActionIcon
						variant="filled"
						color="green"
						loading={isCalling}
						onClick={run}
					>
						<Icon name="chevronRight" />
					</ActionIcon>
				</Group>
				<Checkbox
					label="Show original response"
					checked={showOriginal}
					onChange={(e) => setShowOriginal(e.currentTarget.checked)}
				/>
				<Checkbox
					label="Run as background job"
					checked={runAsBackgroundJob}
					onChange={(e) => setRunAsBackgroundJob(e.currentTarget.checked)}
				/>
				{entries(methodSpec).map(([argumentName, argumentType]) => (
					<Textarea
						key={argumentName}
						required
						label={`${argumentName} (${argumentType})`}
						{...form.getInputProps(argumentName)}
					/>
				))}
				<Group>
					<Button onClick={onDelete} leftIconName="trash" size="md">
						Delete block
					</Button>
				</Group>
			</Stack>
			<Stack
				sx={{
					flex: 1,
				}}
			>
				<Group position="right">
					{resultTooLong && !forcePreview && (
						<Button variant="tertiary" onClick={() => setCollapsed(!collapsed)}>
							{collapsed ? (
								<>
									Expand result <Icon name="chevronDown" />
								</>
							) : (
								<>
									Collapse result <Icon name="chevronUp" />
								</>
							)}
						</Button>
					)}
					{result && language === 'json' && (
						<Button onClick={downloadJson}>Download json</Button>
					)}
					<Select
						value={language}
						onChange={setLanguage}
						searchable
						data={[
							'markup',
							'bash',
							'clike',
							'c',
							'cpp',
							'css',
							'javascript',
							'jsx',
							'coffeescript',
							'actionscript',
							'css-extr',
							'diff',
							'git',
							'go',
							'graphql',
							'handlebars',
							'json',
							'less',
							'makefile',
							'markdown',
							'objectivec',
							'ocaml',
							'python',
							'reason',
							'sass',
							'scss',
							'sql',
							'stylus',
							'tsx',
							'typescript',
							'wasm',
							'yaml',
						]}
					/>
				</Group>

				<Prism
					language={(language as Language) ?? 'json'}
					sx={{
						overflowY: 'auto',
						maxWidth: '50vw',
						minHeight: 200,
					}}
				>
					{modifiedResult}
				</Prism>
			</Stack>
		</Group>
	);
}
